import { Outlet, useMatches } from 'react-router-dom';
import { useEffect } from 'react';
import { useGetMeApi } from 'eds-common/api/v2';
import { Header } from '@/components/Header/Header';
import { StudentHeader } from '@/components/Header/StudentHeader';
import { Navigation } from '@/components/Navigation/Navigation';
import { IdleAlertDialog } from '@/components/IdleAlertDialog/IdleAlertDialog';
import { useMouseflow } from '@/hooks/use-mouseflow';
import { useAppSession } from '@/hooks/use-app-session/use-app-session';
import { ImpersonationBanner } from '@/components/ImpersonationBanner/ImpersonationBanner';
import { useLaunchDarklyIdentify } from '@/hooks/use-launchdarkly-identify';
import { WelcomeModal } from '@/components/WelcomeModal/WelcomeModal';
import studentHomepageBackground from '@/assets/images/student-homepage-background.jpg';

function HeaderLayout({ role }: { role?: string }) {
  return role === 'student' ? (
    <StudentHeader />
  ) : (
    <>
      <ImpersonationBanner />
      <Header />
      <Navigation />
    </>
  );
}

export function AppLayout() {
  const matches = useMatches();
  const { data: user } = useGetMeApi();
  useMouseflow();
  useLaunchDarklyIdentify(user);
  const { startAppSession } = useAppSession();

  useEffect(() => {
    const pageMatches = matches.filter((match) => Boolean(match.handle?.title));
    const lastPageMatch = pageMatches[pageMatches.length - 1];
    if (lastPageMatch) {
      document.title = `${lastPageMatch.handle?.title} - Scholastic Literacy Platform`;
    }
  }, [matches]);

  const isStudent = user?.currentRole === 'student';

  const layout = (
    <>
      <HeaderLayout role={user?.currentRole} />
      <main id="main" className="flex grow flex-col self-stretch">
        <IdleAlertDialog onStaySignedIn={startAppSession} />
        <Outlet />
      </main>
    </>
  );

  return (
    <>
      {isStudent ? (
        <div
          data-testid="student-platform-background"
          className="flex grow flex-col bg-brand-cliffordRed bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${studentHomepageBackground})` }}
        >
          {layout}
        </div>
      ) : (
        layout
      )}
      <WelcomeModal />
    </>
  );
}
